import { z } from 'zod';

// Story 7.3 — Provisioning queue admin schemas

export const provisioningJobItemSchema = z.object({
  id: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  subscriptionEntitlementId: z.string().uuid(),
  action: z.string(),
  status: z.string(),
  externalTenantId: z.string().nullable(),
  externalUserId: z.string().nullable(),
  accessUrl: z.string().nullable(),
  notes: z.string().nullable(),
  attemptsCount: z.number().int(),
  errorMessage: z.string().nullable(),
  createdAt: z.coerce.date(),
  subscription: z.object({
    id: z.string().uuid(),
    client: z.object({
      id: z.string().uuid(),
      name: z.string(),
      email: z.string().nullable(),
    }),
  }),
  subscriptionEntitlement: z.object({
    id: z.string().uuid(),
    saasProduct: z.object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
    }),
    externalPlanSlug: z.string(),
    externalPlanName: z.string(),
    limitsSnapshot: z.unknown(),
  }),
});

export const provisioningJobsResponseSchema = z.object({
  groups: z.array(
    z.object({
      clientId: z.string().uuid(),
      clientName: z.string(),
      clientEmail: z.string().nullable(),
      jobs: z.array(provisioningJobItemSchema),
    }),
  ),
  total: z.number().int(),
});

export const markProvisionedRequestSchema = z
  .object({
    external_tenant_id: z.string().min(1).max(200),
    external_user_id: z.string().min(1).max(200).nullable().optional(),
    access_url: z.string().url().max(500).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();

export const markProvisionedResponseSchema = z.object({
  data: z.object({
    jobId: z.string().uuid(),
    provisioned: z.literal(true),
  }),
});

export type ProvisioningJobItem = z.output<typeof provisioningJobItemSchema>;
export type ProvisioningJobsResponse = z.output<typeof provisioningJobsResponseSchema>;
export type MarkProvisionedRequest = z.input<typeof markProvisionedRequestSchema>;
export type MarkProvisionedResponse = z.output<typeof markProvisionedResponseSchema>;

// Story 12.7 — DLQ de provisioning (painel de falhas).

export const failedProvisioningJobSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  status: z.string(),
  errorMessage: z.string().nullable(),
  attemptsCount: z.number().int(),
  attemptedAt: z.coerce.date().nullable(),
  scheduledAt: z.coerce.date().nullable(),
  entitlementId: z.string().uuid(),
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  saas: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
    })
    .nullable(),
});

export const failedProvisioningJobsResponseSchema = z.object({
  data: z.object({
    items: z.array(failedProvisioningJobSchema),
    nextCursor: z.string().uuid().nullable(),
    total: z.number().int(),
  }),
});

export const reprocessJobResponseSchema = z.object({
  data: z.object({
    jobId: z.string().uuid(),
    reprocessed: z.literal(true),
  }),
});

export const resolveManualRequestSchema = z
  .object({
    resolution: z.enum(['provisioned', 'revoked']),
    externalTenantId: z.string().min(1).max(200).nullable().optional(),
    notes: z.string().trim().min(10).max(2000),
  })
  .strict();

export const resolveManualResponseSchema = z.object({
  data: z.object({
    jobId: z.string().uuid(),
    resolution: z.enum(['provisioned', 'revoked']),
  }),
});

export const ignoreJobRequestSchema = z
  .object({
    reason: z.string().trim().min(10).max(2000),
  })
  .strict();

export const ignoreJobResponseSchema = z.object({
  data: z.object({
    jobId: z.string().uuid(),
    ignored: z.literal(true),
  }),
});

export type FailedProvisioningJob = z.output<typeof failedProvisioningJobSchema>;
export type FailedProvisioningJobsResponse = z.output<typeof failedProvisioningJobsResponseSchema>;
export type ReprocessJobResponse = z.output<typeof reprocessJobResponseSchema>;
export type ResolveManualRequest = z.input<typeof resolveManualRequestSchema>;
export type ResolveManualResponse = z.output<typeof resolveManualResponseSchema>;
export type IgnoreJobRequest = z.input<typeof ignoreJobRequestSchema>;
export type IgnoreJobResponse = z.output<typeof ignoreJobResponseSchema>;

// Story 12.10 — override manual de acesso (suspend/reactivate/status).

export const suspendAccessRequestSchema = z
  .object({
    justification: z.string().trim().min(20).max(2000),
    entitlementIds: z.array(z.string().uuid()).optional(),
  })
  .strict();

export const reactivateAccessRequestSchema = z
  .object({
    justification: z.string().trim().min(20).max(2000),
  })
  .strict();

export const overrideAccessResponseSchema = z.object({
  data: z.object({
    clientId: z.string().uuid(),
    jobsCreated: z.number().int(),
    warning: z.string().optional(),
  }),
});

export const accessStatusResponseSchema = z.object({
  data: z.object({
    clientStatus: z.string(),
    subscriptions: z.array(
      z.object({
        id: z.string().uuid(),
        status: z.string(),
        entitlements: z.array(
          z.object({
            id: z.string().uuid(),
            saasSlug: z.string().nullable(),
            provisioningStatus: z.string(),
          }),
        ),
      }),
    ),
    pendingJobs: z.number().int(),
    failedJobs: z.number().int(),
  }),
});

export type SuspendAccessRequest = z.input<typeof suspendAccessRequestSchema>;
export type ReactivateAccessRequest = z.input<typeof reactivateAccessRequestSchema>;
export type OverrideAccessResponse = z.output<typeof overrideAccessResponseSchema>;
export type AccessStatusResponse = z.output<typeof accessStatusResponseSchema>;
